import React from 'react';

import { BaseInput, Button, Grid } from '@iris/react-components';
import clsx from 'clsx';
import _get from 'lodash/get';

import { DisclaimerText, Popup, SubHeading } from 'reaxl';
import { StandardEmailInput } from 'reaxl-forms';

import { subscribe } from 'axl-config';

/**
 * EmailSubscribeWithoutAlertForm renders an email subscription form with flexible layout options.
 *
 * This component renders in two different layouts based on the `isNotInlineForm` prop:
 * - **Inline Layout (default)**: Renders as a single row with email input and submit button side by side
 * - **Non-Inline Layout**: Renders as two rows with email input on top and submit button below
 *
 * The layout adaptation makes the form suitable for different contexts like inline page sections
 * or modal/sheet overlays where vertical space is preferred over horizontal space.
 *
 * @component
 * @example
 * // Inline layout (single row) - default behavior
 * <EmailSubscribeWithoutAlertForm
 *   title="Subscribe for Updates"
 *   email="user@example.com"
 *   onEmailInputChange={handleEmailChange}
 *   isNotInlineForm={false}
 * />
 *
 * // Non-inline layout (two rows) - for modals/sheets
 * <EmailSubscribeWithoutAlertForm
 *   title="Get Price Alerts"
 *   email=""
 *   onEmailInputChange={handleEmailChange}
 *   isNotInlineForm={true}
 * />
 *
 * @param {Object} props - The component props
 * @param {string|React.ReactNode} [props.title] - Optional title displayed above the form
 * @param {Object} [props.user] - User object containing emailAddress property
 * @param {string} [props.email] - Default email value (fallback if user.emailAddress not available)
 * @param {Function} [props.onEmailInputChange] - Callback function when email input value changes
 * @param {Object} [props.emailSubscribeSend] - Additional props passed to the submit button
 * @param {boolean} [props.isNotInlineForm=false] - Controls form layout:
 *   - `false` (default): Single row layout with email input (7 columns) and button (5 columns)
 *   - `true`: Two row layout with email input (full width, row 1) and button (full width, row 2)
 *
 * @returns {React.ReactNode} The rendered email subscription form
 *
 * @description
 * **Layout Details:**
 * - **Single Row (isNotInlineForm=false)**: Uses Grid with 1 row, email spans 7 columns, button spans 5 columns
 * - **Two Row (isNotInlineForm=true)**: Uses Grid with 2 rows, both email and button span full 12 columns
 * - **Button Styling**: Primary variant for non-inline, tertiary variant for inline
 * - **Spacing**: Horizontal gap applied only in single row layout
 *
 * **Features:**
 * - Email validation with required field
 * - Privacy statement with popup link
 * - Hidden honeypot field for spam protection (textPot input)
 * - Responsive design with appropriate column spans
 * - Includes a hidden input field (`textPot`) for bot detection and form security
 */
export default function EmailSubscribeWithoutAlertForm({
    title,
    user,
    email,
    onEmailInputChange,
    emailSubscribeSend,
    isNotInlineForm,
}) {

    const buttonVariant = isNotInlineForm ? 'primary' : 'tertiary';

    return (
        <div>
            {title && (
                <SubHeading
                    level={3}
                    className="text-bold margin-bottom-2"
                >
                    {title}
                </SubHeading>
            )}
            <Grid
                columns={12}
                rows={!isNotInlineForm ? 1 : 2}
                className={clsx('iris:text-left', { 'iris:gap-x-2': !isNotInlineForm })}
            >
                <div className={isNotInlineForm ? 'iris:px-2 iris:col-span-12' : 'iris:col-span-7'}>
                    <StandardEmailInput
                        maxLength="45"
                        name="emailAddress"
                        placeholder="Email"
                        label=""
                        required
                        value={_get(user, 'emailAddress', email)}
                        onChange={onEmailInputChange}
                    />
                </div>
                <Button
                    className={clsx('iris:w-full iris:font-bold!', {
                        // need ! atc overides font styling
                        'iris:text-[var(--comp-color-content-button-primary-rest)]! iris:col-span-12': isNotInlineForm,
                        'iris:text-[var(--comp-color-content-button-tertiary-rest)]! iris:col-span-5': !isNotInlineForm,
                    })}
                    variant={buttonVariant}
                    formNoValidate
                    type="submit"
                    key="subscribe-button"
                    size="md"
                    {...emailSubscribeSend}
                >
                    Subscribe
                </Button>
            </Grid>
            <DisclaimerText
                withContrast
                size={100}
            >
                <span>
                    {subscribe.inline.privacyStatement.text}
                    <Popup
                        width={600}
                        height={400}
                        url={subscribe.privacyStatement.href}
                    >
                        <span className="text-link margin-left-1">
                            {subscribe.privacyStatement.label}
                        </span>
                    </Popup>
                    .
                </span>
            </DisclaimerText>

            <BaseInput
                type="hidden"
                name="textPot"
                id="textPot"
                validations={{
                    minLength: 0,
                }}
            />
        </div>
    );
}
